import React, { ComponentType } from 'react';
import { Navigate } from 'react-router-dom';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { LoaderGlobal } from '@bloomays-lib/ui.shared';
import { Role } from '@bloomays-lib/types.shared';
import { useAuthStore, useUserStore } from '../stores';
import { isAuth0TokenRefreshError } from '@bloomays-lib/common.auth/browser';
interface ProtectedRouteProps {
  Component: ComponentType;
}

/**
 * Rôles qui ouvrent l'accès à cet espace : l'équipe interne (Admin / System) et les freelances
 * placés en mission (Bloomer).
 *
 * Le rôle Talent n'en fait pas partie : être identifié comme talent dans l'ATS ne donne aucun droit
 * sur l'application (TEC-3305). Le rôle Client non plus : les contacts client accèdent aujourd'hui
 * aux seuls parcours CRA / satisfaction par lien magique, reconnus en amont via `sub === 'Anonymous'`
 * — une connexion Client par Auth0 reste une évolution à venir.
 */
const ROLES_WITH_APP_ACCESS: Role[] = [Role.Admin, Role.System, Role.Bloomer];

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ Component }: ProtectedRouteProps): React.JSX.Element => {
  const auth = useAuthStore((state) => state.auth);
  const bloomaysUser = useUserStore((state) => state.user)?.bloomer;
  const authRoles = auth?.user?.roles || [];
  const authUser = auth?.user;
  if (!auth?.isAuthenticated) {
    if (auth?.error && isAuth0TokenRefreshError(auth.error) && auth?.logout) {
      auth?.logout();
      return <LoaderGlobal waitingText="Session expirée, redirection vers la connexion..." />;
    }
    auth?.loginWithRedirect({
      appState: { returnTo: `${window.location.pathname}${window.location.search}` },
    });
    return <LoaderGlobal waitingText="Redirection vers l'authentification" />;
  }

  if (authUser?.sub === 'Anonymous') {
    return <Component />;
  }
  if (auth?.isAuthenticated && authUser?.email_verified === false) {
    return <Navigate to="/forbidden/email-not-verified" />;
  }

  // Le rôle « Anonymous » n'existe plus : n'avoir aucun rôle applicatif (email inconnu non résolu)
  // ou n'avoir que des rôles hors périmètre de cet espace (Talent, Client) vaut « non reconnu ». On
  // renvoie vers not-recognized — sauf pendant le parcours d'invitation (/invite/:token), où l'on
  // doit laisser passer pour que checkInvitationToken capture l'email.
  const isOnInvitationFlow = window.location.pathname.startsWith('/invite/');
  const hasAppAccess = authRoles.some((role) => ROLES_WITH_APP_ACCESS.includes(role));
  if (auth?.isAuthenticated && !hasAppAccess && !isOnInvitationFlow) {
    return <Navigate to="/forbidden/not-recognized" />;
  }
  const invitation = bloomaysUser?.invitation;
  if (invitation) {
    if (invitation.status !== 'done' && !(window.location.href.indexOf(`${invitation.token}`) > -1)) {
      return <Navigate to={`/invite/${invitation.token}`} />;
    }
  }

  const ComponentAuthenticate = withAuthenticationRequired(Component, {
    onRedirecting: () => <LoaderGlobal waitingText="Redirection vers l'authentification" />,
  });
  return <ComponentAuthenticate />;
};
export default ProtectedRoute;
